import { createStore } from "vuex";
import { auth, db } from "@/firebase";
import { signInAnonymously, updateProfile } from "firebase/auth";
import {
  collection,
  onSnapshot,
  doc,
  getDocs,
  runTransaction,
  setDoc,
} from "firebase/firestore";
import animax from "@animax/store";
import habitats from "@habitats/store";
import scan from "@scan/store";
import { initPlayer } from "@/utils";

export default createStore({
  modules: {
    animax,
    habitatsModule: habitats,
    scan,
  },
  state: {
    currentUser: undefined,
    player: null,
    animals: [],
    habitats: [],
    isSoundActivatedInGame: true,
    isMenuOpen: false,
  },
  mutations: {
    SET_CURRENT_USER(state, user) {
      state.currentUser = user;
    },
    SET_PLAYER(state, player) {
      if (!player.tokens) {
        player.tokens = state.player?.tokens;
      }
      state.player = player;
    },
    SET_ANIMALS(state, animals) {
      state.animals = animals;
    },
    SET_HABITATS(state, habitats) {
      state.habitats = habitats;
    },
    SET_IS_MENU_OPEN(state, open) {
      state.isMenuOpen = open;
    },
  },
  actions: {
    async setUsername({ state }, username) {
      if (!state.currentUser) return;
      await updateProfile(state.currentUser, { displayName: username });
      const playerRef = doc(db, "players", state.currentUser.uid);
      await setDoc(playerRef, { username }, { merge: true });
      // A11 : profil public (ce que les autres joueurs voient).
      await setDoc(doc(db, "profiles", state.currentUser.uid), { username }, { merge: true });
    },

    fetchCurrentUser({ commit }) {
      auth.onAuthStateChanged(async (user) => {
        if (user) {
          commit("SET_CURRENT_USER", user);
          return;
        }
        try {
          await signInAnonymously(auth);
        } catch (error) {
          console.error("[auth] signInAnonymously failed:", error);
          commit("SET_CURRENT_USER", null);
        }
      });
    },

    fetchPlayer({ commit, state }) {
      const docRef = doc(db, "players", state.currentUser.uid);
      return onSnapshot(
        docRef,
        async (snap) => {
          if (!snap.exists()) {
            // A9 (06/10) : avant, un setDoc SANS fusion créait le joueur. Si le
            // lot du premier scan (âme + collection) arrivait juste avant, il
            // était écrasé par { animals: {} } : âme perdue. On attend la
            // réponse du serveur (le cache local dit « absent » sur un appareil
            // neuf) et on ne crée que si le document manque vraiment.
            if (snap.metadata.fromCache) return;
            try {
              await runTransaction(db, async (t) => {
                if (!(await t.get(docRef)).exists()) {
                  t.set(docRef, initPlayer());
                  // A11 : profil public vide créé avec le joueur.
                  t.set(doc(db, "profiles", state.currentUser.uid), {}, { merge: true });
                }
              });
            } catch (error) {
              console.error("[player] création impossible :", error);
            }
            return;
          }
          commit("SET_PLAYER", snap.data());
        },
        (error) => console.error("[player] écoute interrompue :", error)
      );
    },

    async fetchAnimals({ commit }) {
      const querySnapshot = await getDocs(collection(db, "animals"));
      const animals = [];
      querySnapshot.forEach((doc) => {
        animals.push({ ...doc.data(), id: doc.id });
      });
      animals.sort((a, b) => parseInt(a.id) - parseInt(b.id));
      commit("SET_ANIMALS", animals);
    },

    async fetchHabitats({ commit }) {
      const querySnapshot = await getDocs(collection(db, "habitats"));
      const habitats = [];
      querySnapshot.forEach((doc) => {
        habitats.push({ ...doc.data(), id: doc.id });
      });
      commit("SET_HABITATS", habitats);
    },
    async updatePlayerAvatar({ commit, state }, avatarId) {
      const playerRef = doc(db, "players", state.currentUser.uid);
      await setDoc(playerRef, { avatarId }, { merge: true });
      await setDoc(doc(db, "profiles", state.currentUser.uid), { avatarId }, { merge: true });
    }
  },
  getters: {
    getAnimaxById: (state) => (id) => {
      return state.animals.find((animal) => animal.id === id) ?? {};
    },
    getAnimaxByEnvironments: (state) => (environments) => {
      const envToInclude = new Set(environments);
      return state.animals.filter((animax) => {
        const animaxEnv = new Set(animax.environments);
        return envToInclude.intersection(animaxEnv).size > 0;
      });
    },
    getAnimaxByHabitat: (state) => (habitatID) => {
      return state.animals.filter((animax) => animax.habitat.id === habitatID);
    },
    getHabitatById: (state) => (id) => {
      return state.habitats.find((habitat) => habitat.id === id) ?? {};
    },
    getPlayerTokens: (state) => {
      return state.player?.tokens ?? [];
    },
    getTotalAmountOfAnimals: (state) => {
      return state.animals?.length;
    },
    getPlayerScore: (state) => {
      return state.player ? Object.keys(state.player.animals).length : 0;
    },
    getPlayerAnimaxList: (state) => {
      return state.player?.animals ?? {};
    },
    getPlayerHabitatsList: (state) => {
      return state.player?.habitats ?? {};
    },
    getPlayerAnimax: (state) => (animaxId) => {
      return state.player?.animals[animaxId];
    },
    getPendingScansCount: (state) => (animaxId) => {
      return state.player?.animals?.[animaxId]?.pendingScans?.length ?? 0;
    },
    getUnreadScansTotal: (state) => {
      const animals = state.player?.animals;
      if (!animals) return 0;
      return Object.values(animals).reduce(
        (sum, entry) => sum + (entry?.pendingScans?.length ?? 0),
        0
      );
    },
  },
});
